import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Accessibility, Bell, Compass, Globe, Palette, PhoneCall, ShieldCheck, UserRound } from 'lucide-react';
import { useT } from '../../../lib/i18n';
import { usePreferences } from '../preferences/PreferencesContext';
import { PageHeading } from '../ui';
import AccessibilitySection from './sections/AccessibilitySection';
import AppearanceSection from './sections/AppearanceSection';
import CallingSection from './sections/CallingSection';
import LanguageSection from './sections/LanguageSection';
import NotificationsSection from './sections/NotificationsSection';
import ProfileSection from './sections/ProfileSection';
import SecuritySection from './sections/SecuritySection';
import './settings.css';

export const SECTIONS = [
  { key: 'profile', title: 'Profile', sub: 'Manage your personal and company details', Icon: UserRound, tone: 'green', Component: ProfileSection },
  { key: 'appearance', title: 'Appearance', sub: 'Theme, display and visual preferences', Icon: Palette, tone: 'purple', Component: AppearanceSection },
  { key: 'accessibility', title: 'Accessibility', sub: 'Text size, contrast and viewing options', Icon: Accessibility, tone: 'blue-solid', Component: AccessibilitySection },
  { key: 'language', title: 'Language', sub: 'Choose your preferred language', Icon: Globe, tone: 'green', Component: LanguageSection },
  { key: 'notifications', title: 'Notifications', sub: 'Manage alerts and notifications', Icon: Bell, tone: 'red', Component: NotificationsSection },
  { key: 'calling', title: 'Calling Preferences', sub: 'Outbound and inbound call settings', Icon: PhoneCall, tone: 'blue', Component: CallingSection },
  { key: 'security', title: 'Security', sub: 'Password, 2FA and account security', Icon: ShieldCheck, tone: 'orange', Component: SecuritySection },
];

/** Settings: the list of areas on the left, the chosen one on the right (/settings/<area>). */
export default function SettingsPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const { section } = useParams();
  const { loaded } = usePreferences();
  const current = SECTIONS.find((s) => s.key === section);
  if (!current) return <Navigate to="/settings/profile" replace />;
  const { Component } = current;

  return (
    <section className="page-view st-page">
      <PageHeading title={t('Settings')} subtitle={t('Manage your account, preferences and workspace settings.')}
        root={t('Workspace')} crumbs={[t('Settings'), t(current.title)]} />
      <div className="st-layout">
        <nav className="panel st-nav" aria-label={t('Settings')}>
          {SECTIONS.map(({ key, title, sub, Icon, tone }) => (
            <button type="button" key={key} className={`st-nav-item ${key === current.key ? 'on' : ''}`} aria-current={key === current.key ? 'page' : undefined}
              onClick={() => navigate(`/settings/${key}`)}>
              <span className={`st-icon ${tone}`}><Icon size={19} /></span>
              <span><b>{t(title)}</b><small>{t(sub)}</small></span>
            </button>
          ))}
          <button type="button" className="st-nav-item" onClick={() => navigate('/?tour=replay')}>
            <span className="st-icon green"><Compass size={19} /></span>
            <span><b>{t('Welcome Tour')}</b><small>{t('Take the dashboard tour again')}</small></span>
          </button>
        </nav>
        {/* Sections start from the saved choices, so they wait until those have loaded. */}
        <div className="panel st-content">{loaded ? <Component key={current.key} /> : <p className="st-muted">{t('Loading…')}</p>}</div>
      </div>
    </section>
  );
}
